import { inject, Injectable } from '@angular/core';
import { LocationAccessStatusEnum } from 'src/app/enums/location-access-status.enum';
import { GeoCoordinates } from 'src/app/interfaces/geo-coordinates.interface';
import { LocationAccessRequestResult, PlatformService } from './platform.service';

export class LocationPermissionError extends Error {
  readonly status: LocationAccessRequestResult['status_after'];

  constructor(status: LocationAccessRequestResult['status_after']) {
    super(`Location permission is ${status}`);
    this.name = 'LocationPermissionError';
    this.status = status;
  }
}

@Injectable({ providedIn: 'root' })
export class GeolocationService {
  private platform = inject(PlatformService);

  async getCurrentPosition(): Promise<GeoCoordinates> {
    const permission = await this.platform.requestLocationAccess();
    const permission_denied = !permission.granted;
    // When the native module is unavailable, Electron used to fall through to
    // navigator.geolocation and wait up to `timeout` (10s) before IP fallback.
    const native_module_unavailable = permission.status_after === LocationAccessStatusEnum.Unavailable;

    if (permission_denied || native_module_unavailable) {
      throw new LocationPermissionError(permission.status_after);
    }

    return new Promise<GeoCoordinates>((resolve, reject): void => {
      navigator.geolocation.getCurrentPosition(
        (position: GeolocationPosition): void => {
          resolve({
            lat: position.coords.latitude,
            lon: position.coords.longitude,
          });
        },
        (error: GeolocationPositionError): void => {
          reject(error);
        },
        { enableHighAccuracy: true, timeout: 10_000, maximumAge: 60_000 }
      );
    });
  }
}
